import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Container, Typography, Chip, Paper } from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import { API_URL } from "../config";
import { updateStudentCoins } from "../utils/coin";
import { PaystackButton } from "react-paystack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentIcon from "@mui/icons-material/Payment";
import ShieldIcon from "@mui/icons-material/Shield";
import BoltIcon from "@mui/icons-material/Bolt";
import GoldIcon from "../assets/icons/gold.png";
import { toast } from "react-toastify";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: { main: "#F59E0B", light: "#FEF3C7", dark: "#D97706" },
    background: { default: "#FFFBF0", paper: "#ffffff" },
    text: { primary: "#0c1a2e", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: { fontFamily: "'Bricolage Grotesque', 'Sora', sans-serif" },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const coinFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50%       { transform: translateY(-8px) rotate(6deg); }
`;


// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background:
    "linear-gradient(160deg, #FFFBF0 0%, #FEF3C7 30%, #FFF8E7 60%, #FFFBF5 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "32px 20px",
  fontFamily: "'Bricolage Grotesque', sans-serif",
  position: "relative",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -100,
    right: -100,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "fixed",
    bottom: -80,
    left: -80,
    width: 300,
    height: 300,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(245,158,11,0.05) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const PayCard = styled(Paper)(({ theme }) => ({
  borderRadius: 28,
  border: `1px solid ${alpha("#F59E0B", 0.2)}`,
  boxShadow: "0 8px 40px rgba(245,158,11,0.1), 0 2px 8px rgba(0,0,0,0.06)",
  overflow: "hidden",
  maxWidth: 480,
  width: "100%",
  animation: `${fadeUp} 0.5s ease both`,
}));

const CardTopBar = styled(Box)(() => ({
  background:
    "linear-gradient(135deg, #92400E 0%, #D97706 45%, #F59E0B 75%, #FCD34D 100%)",
  padding: "36px 32px 48px",
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    opacity: 0.06,
    backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
    backgroundSize: "32px 32px",
  },
}));

const BackBtn = styled(Box)(() => ({
  position: "absolute",
  left: 18,
  top: 18,
  width: 38,
  height: 38,
  borderRadius: "11px",
  background: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.28)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: "#fff",
  zIndex: 1,
  transition: "all 0.2s ease",
  "&:hover": { background: "rgba(255,255,255,0.28)" },
}));

const CoinImg = styled("img")(() => ({
  width: 72,
  height: 72,
  animation: `${coinFloat} 3s ease-in-out infinite`,
  filter: "drop-shadow(0 6px 12px rgba(0,0,0,0.2))",
  display: "block",
  margin: "0 auto 14px",
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 0",
  borderBottom: `1px solid ${alpha("#000", 0.06)}`,
  "&:last-of-type": { borderBottom: "none" },
}));

const Spinner = styled(Box)(() => ({
  width: 28,
  height: 28,
  border: "3px solid rgba(245,158,11,0.2)",
  borderTop: "3px solid #F59E0B",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
}));

const CancelBtn = styled(Box)(() => ({
  width: "100%",
  padding: "13px",
  borderRadius: 12,
  cursor: "pointer",
  textAlign: "center",
  border: `1px solid ${alpha("#000", 0.12)}`,
  fontFamily: "'Bricolage Grotesque', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  color: "#4B5563",
  background: alpha("#F0F2F8", 0.6),
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha("#000", 0.05),
    borderColor: alpha("#000", 0.18),
  },
}));

const PaystackWrapper = styled(Box)(() => ({
  width: "100%",
  "& button, & .paystack-button, & [class*='paystack']": {
    width: "100% !important",
    padding: "14px 20px !important",
    borderRadius: "12px !important",
    fontFamily: "'Bricolage Grotesque', sans-serif !important",
    fontSize: "15px !important",
    fontWeight: "800 !important",
    background: "linear-gradient(135deg, #D97706, #F59E0B) !important",
    color: "#fff !important",
    border: "none !important",
    cursor: "pointer !important",
    boxShadow: "0 4px 16px rgba(245,158,11,0.35) !important",
    transition: "all 0.2s ease !important",
    letterSpacing: "0.2px !important",
  },
}));

// ─── Main Component ───────────────────────────────────────────────────────────
const PaystackPaymentScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { amount, coins, email, publickey } = location.state || {};

  const [authToken, setAuthToken] = useState(null);
  const [currency, setCurrency] = useState("USD");
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentReference, setPaymentReference] = useState(null);
  const [backendCurrency, setBackendCurrency] = useState(null);

  useEffect(() => {
    const retrieveAuthToken = async () => {
      try {
        const tokenString = localStorage.getItem("authToken");
        if (!tokenString) throw new Error("No authentication token found");
        const token = JSON.parse(tokenString);
        setAuthToken(token);
        const response = await fetch(`${API_URL}/user/country/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        });
        if (!response.ok) throw new Error("Failed to fetch user profile");
        const data = await response.json();
        setCurrency(data.country.currency);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    retrieveAuthToken();
  }, []);

  const numericAmount = parseFloat(amount);

  const generatePaymentReference = async () => {
    if (!authToken) throw new Error("No auth token found");
    const response = await fetch(`${API_URL}/generate-payment-reference/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken.access}`,
      },
      body: JSON.stringify({ amount: numericAmount }),
    });
    if (!response.ok) {
      const text = await response.text();
      console.log("FULL BACKEND RESPONSE:", text);
      throw new Error("Failed to generate payment reference");
    }
    return response.json();
  };

  useEffect(() => {
    const preparePayment = async () => {
      try {
        const data = await generatePaymentReference();
        setPaymentReference(data.reference);
        setBackendCurrency(data.currency);
      } catch (err) {
        console.error(err);
        setError(err.message);
      }
    };
    if (authToken && numericAmount) preparePayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, numericAmount]);

  const componentProps = {
    email,
    amount: numericAmount * 100,
    currency: backendCurrency || currency,
    publicKey: publickey?.trim(),
    reference: paymentReference,
    text: "Pay Now — Secure",
    onSuccess: async (response) => {
      console.log("Payment successful:", response);
      await updateStudentCoins();
      toast.success(`Payment Successful! You have received ${coins} Golds`);
      navigate("/golds");
    },
    onClose: () => {
      toast.error("Payment Cancelled");
      navigate("/golds");
    },
    className: "custom-paystack-button",
  };

  if (!amount) {
    toast.error("Payment amount is missing. Cannot generate reference.");
    return null;
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <PageWrapper>
          <Box sx={{ textAlign: "center", p: 4 }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: "#DC2626",
                fontFamily: "'Bricolage Grotesque', sans-serif",
                mb: 1,
              }}
            >
              {error}
            </Typography>
            <CancelBtn
              onClick={() => navigate("/golds")}
              sx={{ maxWidth: 200, mx: "auto" }}
            >
              Go Back
            </CancelBtn>
          </Box>
        </PageWrapper>
      </ThemeProvider>
    );
  }

  const activeCurrency = backendCurrency || currency;

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <Container
          maxWidth="sm"
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <PayCard elevation={0}>
            {/* ── Card top ── */}
            <CardTopBar>
              <BackBtn onClick={() => navigate("/golds")}>
                <ArrowBackIcon sx={{ fontSize: 18 }} />
              </BackBtn>

              <Box sx={{ position: "relative", zIndex: 1 }}>
                <CoinImg src={GoldIcon} alt="Gold" />
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: "#fff",
                    fontSize: "1.6rem",
                    letterSpacing: "-0.5px",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    lineHeight: 1,
                    mb: 0.5,
                  }}
                >
                  {coins?.toLocaleString()} Coins
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "0.88rem",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  Confirm your purchase below
                </Typography>
              </Box>
            </CardTopBar>

            {/* ── Card body — pulled up ── */}
            <Box sx={{ mx: 3, mt: -5, mb: 3, position: "relative", zIndex: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  border: `1px solid ${alpha("#F59E0B", 0.15)}`,
                  boxShadow: "0 4px 20px rgba(245,158,11,0.1)",
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <Box sx={{ p: "20px 22px" }}>
                  <DetailRow>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#9CA3AF",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      Amount
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 900,
                        color: "#0c1a2e",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {activeCurrency} {Number(amount).toLocaleString()}
                    </Typography>
                  </DetailRow>

                  <DetailRow>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#9CA3AF",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      You receive
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <img
                        src={GoldIcon}
                        alt="coin"
                        style={{ width: 20, height: 20 }}
                      />
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: "#D97706",
                          fontFamily: "'Bricolage Grotesque', sans-serif",
                        }}
                      >
                        {coins?.toLocaleString()} Coins
                      </Typography>
                    </Box>
                  </DetailRow>

                  <DetailRow>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#9CA3AF",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      Currency
                    </Typography>
                    <Chip
                      label={activeCurrency}
                      size="small"
                      sx={{
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontWeight: 800,
                        fontSize: 12,
                        background: alpha("#F59E0B", 0.1),
                        color: "#D97706",
                        border: `1px solid ${alpha("#F59E0B", 0.25)}`,
                        borderRadius: "8px",
                      }}
                    />
                  </DetailRow>

                  <DetailRow>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#9CA3AF",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      Email
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#4B5563",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {email}
                    </Typography>
                  </DetailRow>
                </Box>
              </Paper>
            </Box>

            {/* ── Actions ── */}
            <Box sx={{ px: 3, pb: 3 }}>
              {paymentReference ? (
                <PaystackWrapper sx={{ mb: 1.5 }}>
                  <PaystackButton {...componentProps} />
                </PaystackWrapper>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    py: 3,
                  }}
                >
                  <Spinner />
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#9CA3AF",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontWeight: 600,
                    }}
                  >
                    Preparing payment…
                  </Typography>
                </Box>
              )}
              <CancelBtn onClick={() => navigate("/golds")}>
                Cancel Payment
              </CancelBtn>
            </Box>

            {/* ── Trust badges ── */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 3,
                pb: 3,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                <ShieldIcon sx={{ fontSize: 13, color: "#059669" }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#9CA3AF",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  SSL Secured
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                <BoltIcon sx={{ fontSize: 13, color: "#F59E0B" }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#9CA3AF",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  Instant delivery
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                <PaymentIcon sx={{ fontSize: 13, color: "#0288D1" }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#9CA3AF",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  Powered by Paystack
                </Typography>
              </Box>
            </Box>
          </PayCard>
        </Container>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default PaystackPaymentScreen;
